import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { SelectButtonModule } from 'primeng/selectbutton';
import { User } from '../../../../modals/user.model';
import { AuthService } from '../../../../services/auth.service';
import { DynamicTemplateConfigService } from '../../../../services/pdf-generation/dynamic-template-config.service';
import { ProductDbService } from '../../../../services/productdb.service';
import { environment } from '../../../app.environment';
import { Subscription } from 'rxjs';
import { BillNumberRefreshService } from '../../services/bill-number-refresh.service';

/**
 * DashboardRightPanelComponent
 *
 * Standalone component that renders the right-side summary panel of the POS dashboard:
 *   - Bill Number flip-counter animation (self-contained: owns WebSocket + state)
 *   - Counter Number display (self-contained: reads from AuthService)
 *   - Process button text (self-contained: derived from mode inputs)
 *   - Totals section (subtotal, taxes, discounts, net amount) — via @Input()
 *   - Overall Discount input (percentage / amount mode) — via @Input() + @Output()
 *   - Process Order / Process Quotation button — via @Output()
 *
 * Bill counter WebSocket, timer, and animation state are fully managed here.
 * All business calculations (totals, taxes, discounts) remain in DashboardComponent
 * and are passed as @Input() values.
 *
 * MOVED FROM DashboardComponent:
 *   - demoBillNumber, billNumberSocket, billNumberReconnectTimer, isBillNumberSocketClosing
 *   - previousBillDigits, isFlipping
 *   - startDemoBillNumberTimer(), fetchLatestBillNo(), connectBillNumberSocket()
 *   - disconnectBillNumberSocket(), handleBillNumberSocketFrame()
 *   - subscribeToBillNumberTopics(), applyBillNumberUpdate()
 *   - updateDisplayedBillNumber(), getBillNumberDigits(), getBillNumber()
 *   - getBillNumberTopics(), getBillNumberWebSocketUrl(), getBillNumberAccessToken()
 *   - getBillNumberUser(), getBillNumberTopicContext()
 *   - normalizeBillNumberTopicSegment(), decodeBillNumberToken(), parseClientId()
 *   - extractNumericBillNumber(), syncBillNumberFromInvoice()
 *   - getCounterNumber(), getProcessButtonText()
 */
@Component({
  selector: 'app-dashboard-right-panel',
  standalone: true,
  imports: [CommonModule, FormsModule, ButtonModule, SelectButtonModule],
  templateUrl: './dashboard-right-panel.component.html',
  styleUrls: ['./dashboard-right-panel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DashboardRightPanelComponent implements OnInit, OnChanges, OnDestroy {

  // ─── Bill Counter State (self-contained — MOVED FROM DashboardComponent) ──
  /** Current bill number (fetched via API, refreshed after sale + polling) */
  private demoBillNumber: number = 0;
  /** Subscription to BillNumberRefreshService.billNumber$ (replaces the WebSocket) */
  private billNumberSubscription: Subscription | null = null;
  // WebSocket bill counter disabled — replaced by BillNumberRefreshService.
  // /** Active WebSocket connection for live bill number updates */
  // private billNumberSocket: WebSocket | null = null;
  // /** Reconnect timer handle */
  // private billNumberReconnectTimer: ReturnType<typeof setTimeout> | null = null;
  // /** Flag to prevent reconnect attempts during intentional disconnect */
  // private isBillNumberSocketClosing = false;

  /** Previous bill digits for flip animation */
  previousBillDigits: number[] = [];
  /** Per-digit flip trigger flags */
  isFlipping: boolean[] = [];

  // ─── Cart Summary Inputs ───────────────────────────────────────────────────
  /** Number of unique product items in cart */
  @Input() uniqueItemsCount: number = 0;

  /** Total quantity of all items in cart */
  @Input() totalQuantity: number = 0;

  @Input() freeQuantity:number =0;

  // ─── Tax & Total Inputs ────────────────────────────────────────────────────
  /** Subtotal before any tax */
  @Input() subtotal: number = 0;

  @Input() totalFreeAmount : number = 0;

  /** Whether this is an inter-state tax transaction (shows IGST vs CGST+SGST) */
  @Input() isInterStateTaxOrder: boolean = false;

  /** IGST amount (inter-state) */
  @Input() igst: number = 0;

  /** CGST amount (intra-state) */
  @Input() cgst: number = 0;

  /** SGST amount (intra-state) */
  @Input() sgst: number = 0;

  /** Sum of all line-level product discounts */
  @Input() productDiscount: number = 0;

  // ─── Overall Discount Inputs ───────────────────────────────────────────────
  /** Whether overall discount feature is enabled for this store */
  @Input() enableOverAllDiscount: boolean = false;

  @Input() isFreeQtyEnabled : boolean = false;

  /** True when in exchange order mode (overall discount is hidden) */
  @Input() isExchangeOrder: boolean = false;

  /** Current overall discount mode */
  @Input() overAllDiscountMode: 'percentage' | 'amount' = 'amount';

  /** Overall discount percentage value */
  @Input() overAllDiscountPercent: number = 0;

  /** Overall discount amount value */
  @Input() overAllDiscount: number = 0;

  /** Options array for the p-selectButton discount mode toggle */
  @Input() discountModeOptions: any[] = [];

  /** Subtotal after line discounts — used as max for amount discount */
  @Input() subtotalAfterLineDiscount: number = 0;

  /** Computed overall discount amount (for display) */
  @Input() overAllDiscountAmount: number = 0;

  // ─── Net Amount Inputs ─────────────────────────────────────────────────────
  /** Round-off adjustment amount */
  @Input() roundOffAmount: number = 0;

  /** Whether this is a Mobile industry session (shows Free Amount) */
  @Input() isMobileIndustry: boolean = false;

  /** Free Amount deduction (Mobile industry only) */
  @Input() freeAmount: number = 0;

  /** Final net/rounded total to pay */
  @Input() roundedTotal: number = 0;

  /**
   * Profit/Margin Amount for the current cart.
   * Computed by DashboardComponent.getProfitAmount() via PosCalculationService.getProfitAmount().
   * Automatically updated on every cart change (quantity, price, discount, removal, hold/resume, etc.)
   */
  @Input() profitAmount: number = 0;

  // ─── Process Order Button Inputs ───────────────────────────────────────────
  /** True when in quotation mode */
  @Input() isQuotationMode: boolean = false;

  /** Whether the quotation can be completed right now */
  @Input() canCompleteQuotation: boolean = false;

  /** True while quotation PDF is being generated */
  @Input() isProcessingQuotation: boolean = false;

  /** True when any cart item is missing a required variant selection */
  @Input() hasPendingVariantSelection: boolean = false;

  /** True when NetworkHealthService blocks orders due to OFFLINE, SLOW (3G), or CRITICAL network */
  @Input() isOrderBlocked: boolean = false;

  /** True when the browser is completely offline */
  @Input() isNetworkOffline: boolean = false;

  /**
   * Mobile industry only: cart contains only free items, so checkout needs
   * no payment — keep the Process Order button enabled despite a 0 total.
   */
  @Input() allowFreeOnlyCheckout: boolean = false;

  // ─── Output Events ─────────────────────────────────────────────────────────
  /** Fired when discount mode toggle (% / ₹) is changed */
  @Output() discountModeChanged = new EventEmitter<any>();

  /** Fired when discount value input blurs with the final parsed value */
  @Output() discountValueChanged = new EventEmitter<number>();

  /** Fired when the discount input loses focus */
  @Output() discountBlurred = new EventEmitter<void>();

  /** Fired when the Process Order / Process Quotation button is clicked */
  @Output() processOrderClicked = new EventEmitter<void>();

  constructor(
    private authService: AuthService,
    private productService: ProductDbService,
    private dynamicTemplateConfigService: DynamicTemplateConfigService,
    private ngZone: NgZone,
    private cdr: ChangeDetectorRef,
    private billNumberRefreshService: BillNumberRefreshService,
  ) {}

  // ─── Overall Discount Raw Input Buffer ────────────────────────────────────
  /**
   * Raw string the user is currently typing into the discount input.
   * We keep this separate from the bound numeric @Input() so that Angular
   * never forcibly overwrites the DOM value mid-keystroke (which causes
   * leading-zero artifacts like "02" and breaks decimal entry).
   */
  discountRawValue: string = '';

  /**
   * Sync the raw buffer from the latest @Input() values.
   * Called by ngOnChanges so the field always shows the current parent value
   * when the mode changes or a reset occurs from outside.
   */
  private lastDiscountMode: string = '';
  private lastDiscountPercent: number = 0;
  private lastDiscountAmount: number = 0;

  ngOnChanges(changes?: SimpleChanges): void {
    const current =
      this.overAllDiscountMode === 'percentage'
        ? this.overAllDiscountPercent
        : this.overAllDiscount;
    const modeChanged = this.overAllDiscountMode !== this.lastDiscountMode;
    const valueChanged =
      this.overAllDiscountPercent !== this.lastDiscountPercent ||
      this.overAllDiscount !== this.lastDiscountAmount;

    // Only update the raw buffer when the value genuinely changed from outside
    // (e.g. mode toggle, reset) — never while the user is mid-type.
    if (modeChanged || valueChanged) {
      this.discountRawValue = current === 0 ? '' : String(current);
      this.lastDiscountMode = this.overAllDiscountMode;
      this.lastDiscountPercent = this.overAllDiscountPercent;
      this.lastDiscountAmount = this.overAllDiscount;
      console.log({
  overAllDiscount: this.overAllDiscount,
  overAllDiscountPercent: this.overAllDiscountPercent,
  current,
});
    }
  }

  /** Called on every keystroke — update local buffer AND emit live to parent. */
  onDiscountInput(event: Event): void {
    const raw = (event.target as HTMLInputElement).value;
    this.discountRawValue = raw;

    const parsed = parseFloat(raw);
    const finalValue = isNaN(parsed) || parsed < 0 ? 0 : parsed;

    // Track the last emitted value so ngOnChanges knows it came from user input
    if (this.overAllDiscountMode === 'percentage') {
      this.lastDiscountPercent = finalValue;
    } else {
      this.lastDiscountAmount = finalValue;
    }

    this.discountValueChanged.emit(finalValue);
  }

  /** Called on blur — format clean string if needed. */
  onDiscountBlur(): void {
    const parsed = parseFloat(this.discountRawValue);
    const finalValue = isNaN(parsed) || parsed < 0 ? 0 : parsed;
    this.discountRawValue = finalValue === 0 ? '' : String(finalValue);
    this.discountBlurred.emit();
  }

  // ─── Lifecycle ─────────────────────────────────────────────────────────────

  ngOnInit(): void {
    this.startDemoBillNumberTimer();
  }

  ngOnDestroy(): void {
    // this.disconnectBillNumberSocket();
    this.billNumberSubscription?.unsubscribe();
    this.billNumberSubscription = null;
  }

  // ─── Process Button Text (MOVED FROM DashboardComponent) ───────────────────

  /**
   * Returns the first 3 uppercase letters of the business/client name.
   * Example: "Elixir Business" → "ELI".
   * Reads from DynamicTemplateConfigService (already cached from API on app load)
   * so no extra HTTP calls are made.
   */
  getBusinessNamePrefix(): string {
    const clientData = this.dynamicTemplateConfigService.getClientData();
    const name: string =
      clientData?.client_name ||
      JSON.parse(localStorage.getItem('storename') || '{}')?.name ||
      '';
    return name.replace(/\s+/g, '').substring(0, 3).toUpperCase();
  }

  /**
   * Returns the label for the process order button based on current mode.
   * MOVED FROM: DashboardComponent.getProcessButtonText()
   */
  getProcessButtonText(): string {
    if (this.isExchangeOrder) {
      return 'Process Return';
    } else if (this.isQuotationMode) {
      return 'Process Quotation';
    } else {
      return 'Process Order';
    }
  }

  // ─── Counter Number (MOVED FROM DashboardComponent) ────────────────────────

  /**
   * Reads the counter name / ID from the active counter session.
   * MOVED FROM: DashboardComponent.getCounterNumber()
   */
  getCounterNumber(): string {
    const counterSession = this.authService.getCounterSession();
    if (counterSession?.billcounter?.name) {
      return counterSession.billcounter.name;
    }
    if (counterSession?.billcounter_id) {
      return counterSession.billcounter_id.toString().padStart(2, '0');
    }
    return '00';
  }

  // ─── Bill Number Digits (MOVED FROM DashboardComponent) ────────────────────

  /**
   * Returns the current bill number as a string (min 4 chars, zero-padded).
   * MOVED FROM: DashboardComponent.getBillNumber()
   */
  getBillNumber(): string {
    return this.demoBillNumber.toString() || '0000';
  }

  /**
   * Splits the bill number into an array of individual digits for the flip counter.
   * MOVED FROM: DashboardComponent.getBillNumberDigits()
   */
  /** Digits keep their slot so a changed digit flips in place */
  trackByIndex = (index: number): number => index;

  getBillNumberDigits(): number[] {
    const billNo = this.getBillNumber();
    const paddedBillNo = billNo.length < 4 ? billNo.padStart(4, '0') : billNo;
    return paddedBillNo.split('').map((d) => parseInt(d, 10));
  }

  // ─── Bill Counter (REST refresh-after-sale + polling) ──────────────────────

  /**
   * Initialises bill digit state and subscribes to BillNumberRefreshService,
   * which fetches the latest bill number on load, after each sale/return on
   * this counter, on a light polling interval, and when the tab is re-focused.
   * MOVED FROM: DashboardComponent.startDemoBillNumberTimer()
   */
  startDemoBillNumberTimer(): void {
    const initialDigits = this.getBillNumberDigits();
    this.previousBillDigits = [...initialDigits];
    this.isFlipping = new Array(initialDigits.length).fill(false);

    // this.fetchLatestBillNo();
    // this.connectBillNumberSocket();
    this.billNumberSubscription?.unsubscribe();
    this.billNumberSubscription = this.billNumberRefreshService.billNumber$
      .subscribe((billNumber) => this.updateDisplayedBillNumber(billNumber));
  }

  // ─── Previous WebSocket implementation (disabled, kept for reference) ──────

  // /**
   // * Fetches the latest bill number from the REST API on component init.
   // * MOVED FROM: DashboardComponent.fetchLatestBillNo()
   // */
  // private fetchLatestBillNo(): void {
    // this.productService.getLatestBillNo().subscribe({
      // next: (response) => {
        // if (response?.data?.count !== undefined) {
          // this.updateDisplayedBillNumber(response.data.count);
        // }
      // },
      // error: (error) => {
        // console.error('Failed to fetch latest bill number:', error);
      // },
    // });
  // }

  // /**
   // * Opens the WebSocket connection and registers all frame handlers.
   // * MOVED FROM: DashboardComponent.connectBillNumberSocket()
   // */
  // private connectBillNumberSocket(): void {
    // const token = this.getBillNumberAccessToken();
    // const wsUrl = this.getBillNumberWebSocketUrl();

    // if (!token || !wsUrl || this.billNumberSocket) {
      // return;
    // }

    // this.isBillNumberSocketClosing = false;
    // this.billNumberSocket = new WebSocket(wsUrl);

    // this.billNumberSocket.onopen = () => {
      // const connectFrame = [
        // 'CONNECT',
        // 'accept-version:1.2',
        // 'heart-beat:0,0',
        // `login:${token}`,
        // '',
        // '\0',
      // ].join('\n');

      // this.billNumberSocket?.send(connectFrame);
    // };

    // this.billNumberSocket.onmessage = (event) => {
      // const frames = String(event.data)
        // .split('\0')
        // .map((frame) => frame.trim())
        // .filter(Boolean);

      // frames.forEach((frame) => this.handleBillNumberSocketFrame(frame));
    // };

    // this.billNumberSocket.onclose = () => {
      // this.billNumberSocket = null;

      // if (!this.isBillNumberSocketClosing) {
        // this.billNumberReconnectTimer = setTimeout(() => {
          // this.connectBillNumberSocket();
        // }, 5000);
      // }
    // };

    // this.billNumberSocket.onerror = (error) => {
      // console.error('Bill number websocket error:', error);
    // };
  // }

  // /**
   // * Parses a raw STOMP WebSocket frame and dispatches the appropriate handler.
   // * MOVED FROM: DashboardComponent.handleBillNumberSocketFrame()
   // */
  // private handleBillNumberSocketFrame(frame: string): void {
    // const [commandLine, ...rest] = frame.split('\n');
    // const command = commandLine.trim();
    // const emptyLineIndex = rest.findIndex((line) => line.trim() === '');
    // const headerLines =
      // emptyLineIndex >= 0 ? rest.slice(0, emptyLineIndex) : rest;
    // const bodyLines = emptyLineIndex >= 0 ? rest.slice(emptyLineIndex + 1) : [];

    // if (command === 'CONNECTED') {
      // this.subscribeToBillNumberTopics();
      // return;
    // }

    // if (command !== 'MESSAGE') {
      // if (command === 'ERROR') {
        // console.error(
          // 'Bill number websocket STOMP error:',
          // bodyLines.join('\n'),
        // );
      // }
      // return;
    // }

    // const headers = headerLines.reduce(
      // (acc, line) => {
        // const separatorIndex = line.indexOf(':');
        // if (separatorIndex > -1) {
          // const key = line.slice(0, separatorIndex).trim();
          // const value = line.slice(separatorIndex + 1).trim();
          // acc[key] = value;
        // }
        // return acc;
      // },
      // {} as Record<string, string>,
    // );

    // const subscription = headers['subscription'];
    // const body = bodyLines.join('\n').trim();

    // if (subscription && body) {
      // this.applyBillNumberUpdate(body);
    // }
  // }

  // /**
   // * Sends STOMP SUBSCRIBE frames for all bill-number topics after a CONNECTED event.
   // * MOVED FROM: DashboardComponent.subscribeToBillNumberTopics()
   // */
  // private subscribeToBillNumberTopics(): void {
    // const topics = this.getBillNumberTopics();

    // topics.forEach((topic, index) => {
      // const subscribeFrame = [
        // 'SUBSCRIBE',
        // `id:bill-number-${index}`,
        // `destination:${topic}`,
        // '',
        // '\0',
      // ].join('\n');

      // this.billNumberSocket?.send(subscribeFrame);
    // });
  // }

  // /**
   // * Parses the bill number from an incoming WebSocket MESSAGE body and triggers
   // * the flip animation.
   // * MOVED FROM: DashboardComponent.applyBillNumberUpdate()
   // */
  // private applyBillNumberUpdate(messageBody: string): void {
    // try {
      // const payload = JSON.parse(messageBody);
      // const parsedBillNumber = this.extractNumericBillNumber(
        // payload?.billNo,
        // payload?.billno,
        // payload?.billNumber,
        // payload?.invoiceNo,
        // payload?.invoiceno,
        // payload?.invoiceinc,
        // payload?.count,
      // );

      // if (parsedBillNumber === null) {
        // return;
      // }
      // console.log(
        // payload,
        // 'Parsed bill number from websocket:',
        // parsedBillNumber,
      // );
      // this.updateDisplayedBillNumber(parsedBillNumber);
    // } catch (error) {
      // console.error('Failed to parse bill number websocket payload:', error);
    // }
  // }

  /**
   * Updates `demoBillNumber` and triggers the per-digit flip animation.
   * MOVED FROM: DashboardComponent.updateDisplayedBillNumber()
   */
  private updateDisplayedBillNumber(parsedBillNumber: number): void {
    this.ngZone.run(() => {
      const oldDigits = this.getBillNumberDigits();
      this.demoBillNumber = parsedBillNumber;
      const newDigits = this.getBillNumberDigits();

      if (newDigits.length > this.isFlipping.length) {
        this.isFlipping = new Array(newDigits.length).fill(false);
        this.previousBillDigits = [...oldDigits];
      }

      newDigits.forEach((digit, index) => {
        if (digit !== oldDigits[index]) {
          this.isFlipping[index] = true;
          setTimeout(() => {
            this.isFlipping[index] = false;
            this.previousBillDigits[index] = digit;
            this.cdr.markForCheck();
          }, 500);
        }
      });

      this.cdr.markForCheck();
    });
  }

  // /**
   // * Resolves the list of STOMP destination topics to subscribe to.
   // * MOVED FROM: DashboardComponent.getBillNumberTopics()
   // */
  // private getBillNumberTopics(): string[] {
    // const topicContext = this.getBillNumberTopicContext();

    // if (!topicContext) {
      // return [];
    // }

    // return [
      // `/topic/sales-count/${topicContext.clientId}/${topicContext.storeId}`,
    // ];
  // }

  // /**
   // * Builds the WebSocket server URL from the configured API base URL.
   // * MOVED FROM: DashboardComponent.getBillNumberWebSocketUrl()
   // */
  // private getBillNumberWebSocketUrl(): string {
    // const apiBaseUrl = environment.apiURL.replace(/\/api\/?$/, '');

    // if (apiBaseUrl.startsWith('https://')) {
      // return `${apiBaseUrl.replace('https://', 'wss://')}/ws`;
    // }

    // if (apiBaseUrl.startsWith('http://')) {
      // return `${apiBaseUrl.replace('http://', 'ws://')}/ws`;
    // }

    // return '';
  // }

  // /**
   // * Extracts the JWT access token for the bill number WebSocket.
   // * MOVED FROM: DashboardComponent.getBillNumberAccessToken()
   // */
  // private getBillNumberAccessToken(): string {
    // return this.getBillNumberUser()?.accessToken || '';
  // }

  // /**
   // * Reads the current authenticated user from the AuthService / localStorage.
   // * MOVED FROM: DashboardComponent.getBillNumberUser()
   // */
  // private getBillNumberUser(): User | null {
    // const currentUser = this.authService.currentUserValue;
    // if (currentUser?.accessToken) {
      // return currentUser;
    // }

    // try {
      // const storedUser = localStorage.getItem('name');
      // return storedUser ? (JSON.parse(storedUser) as User) : null;
    // } catch {
      // return null;
    // }
  // }

  // /**
   // * Builds the client/store context for the STOMP topic path.
   // * MOVED FROM: DashboardComponent.getBillNumberTopicContext()
   // */
  // private getBillNumberTopicContext(): {
    // clientId: string;
    // storeId: string;
  // } | null {
    // const user = this.getBillNumberUser();
    // const decodedToken = this.decodeBillNumberToken(user?.accessToken);

    // const clientId = this.normalizeBillNumberTopicSegment(
      // user?.client_id ??
      // decodedToken?.['client_id'],
    // );

    // const storeId = this.normalizeBillNumberTopicSegment(
      // user?.store_id ??
      // decodedToken?.['store'] ??
      // decodedToken?.['store_id'],
    // );

    // if (!clientId || !storeId) {
      // return null;
    // }

    // return { clientId, storeId };
  // }

  // /**
   // * MOVED FROM: DashboardComponent.normalizeBillNumberTopicSegment()
   // */
  // private normalizeBillNumberTopicSegment(value: unknown): string | null {
    // if (value === undefined || value === null) {
      // return null;
    // }

    // const normalizedValue = String(value).trim();
    // return normalizedValue ? normalizedValue : null;
  // }

  // /**
   // * Decodes the JWT payload to extract topic context claims.
   // * MOVED FROM: DashboardComponent.decodeBillNumberToken()
   // */
  // private decodeBillNumberToken(token?: string): Record<string, any> | null {
    // if (!token) {
      // return null;
    // }

    // try {
      // const payload = token.split('.')[1];
      // const normalizedPayload = payload.replace(/-/g, '+').replace(/_/g, '/');
      // const decodedPayload = atob(normalizedPayload);

      // return JSON.parse(decodedPayload);
    // } catch {
      // return null;
    // }
  // }

  // /**
   // * Tries each candidate value in order and returns the first valid number found.
   // * MOVED FROM: DashboardComponent.extractNumericBillNumber()
   // */
  // private extractNumericBillNumber(...candidates: unknown[]): number | null {
    // return candidates.reduce<number | null>((matchedValue, candidate) => {
      // if (
        // matchedValue !== null ||
        // candidate === undefined ||
        // candidate === null ||
        // candidate === ''
      // ) {
        // return matchedValue;
      // }

      // const numericCandidate = Number(candidate);
      // return Number.isNaN(numericCandidate) ? null : numericCandidate;
    // }, null);
  // }

  // /**
   // * Gracefully closes the WebSocket connection and cancels any pending reconnect timer.
   // * MOVED FROM: DashboardComponent.disconnectBillNumberSocket()
   // */
  // private disconnectBillNumberSocket(): void {
    // this.isBillNumberSocketClosing = true;

    // if (this.billNumberReconnectTimer) {
      // clearTimeout(this.billNumberReconnectTimer);
      // this.billNumberReconnectTimer = null;
    // }

    // if (this.billNumberSocket) {
      // if (this.billNumberSocket.readyState === WebSocket.OPEN) {
        // this.billNumberSocket.send(['DISCONNECT', '', '\0'].join('\n'));
      // }

      // this.billNumberSocket.close();
      // this.billNumberSocket = null;
    // }
  // }
}
